import React, { useMemo, useRef, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import AgGrid from '../../components/ag-grid/ag-grid';
import RenderControls from '../../components/ra-list/RenderControls';
import { dayjs, formatDateTime } from '../../components/ex_dayjs';
import type { FilterControl, MasterViewRow } from './types';
import DetailsDialog from './details-dialog';

const MasterView: React.FC = () => {
    const refInput = useRef<any>(null);
    const refbtnFilter = useRef<HTMLElement>(null);
    const [selectedEvent, setSelectedEvent] = useState<MasterViewRow | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const defaultColDef = useMemo<ColDef>(() => ({
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 80,
        filter: false,
        suppressMovable: true,
    }), []);

    const colDefs: ColDef[] = [
        { field: 'eventId', headerName: 'Event ID', maxWidth: 130 },
        { field: 'eventSiteId', headerName: 'Site ID', maxWidth: 120 },
        {
            field: 'eventName', headerName: 'Event', minWidth: 200,
            cellRenderer: (params: any) => {
                if (!params.value) return '';
                if (!params.data?.eventUrl) return params.value;
                return <a href={params.data.eventUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}>{params.value}</a>;
            }
        },
        { field: 'eventVenue', headerName: 'Venue', minWidth: 150 },
        {
            field: 'eventDateTime', headerName: 'Event Date', maxWidth: 160,
            valueFormatter: (params: any) => params.value ? formatDateTime(params.value) : ''
        },
        {
            field: 'lastModified', headerName: 'Last Modified', maxWidth: 160,
            valueFormatter: (params: any) => params.value ? dayjs.utc(params.value).fromNow() : ''
        },
        {
            field: 'earliestModified', headerName: 'Earliest Modified', maxWidth: 160,
            valueFormatter: (params: any) => params.value ? dayjs.utc(params.value).fromNow() : ''
        },
        { field: 'listingCount', headerName: 'Listing Count', maxWidth: 130 },
    ];

    const controls: FilterControl[] = [
        { component: 'number', name: 'eventId_eq', label: 'Event ID', xs: 0.8 },
        { component: 'text', name: 'eventSiteId', label: 'Site ID', xs: 1 },
        { component: 'text', name: 'eventName', label: 'Event Name', xs: 1.5 },
        { component: 'text', name: 'eventVenue', label: 'Venue', xs: 1.5 },
    ];

    const handleSearch = (filterList: Record<string, unknown>): void => {
        refInput.current.customFilterSearch(filterList);
    };

    const onRowClicked = (params: any): void => {
        setSelectedEvent(params.data as MasterViewRow);
        setDialogOpen(true);
    };

    return (
        <>
            <RenderControls
                controls={controls}
                onSearch={handleSearch}
                refFilter={refbtnFilter}
                filterInline={true}
                agSource='AdditionalListings'
                agSort={{ field: 'lastModified', order: 'desc' }}
            />
            <AgGrid
                ref={refInput}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                rowHeight={45}
                onRowClicked={onRowClicked}
                list={'AdditionalListings'}
                listAction={'MasterView'}
                defaultValues={{ sortField: { field: 'lastModified', order: 'desc' } }}
            />
            {selectedEvent && (
                <DetailsDialog
                    event={selectedEvent}
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                />
            )}
        </>
    );
};

export default MasterView;
